import React, { useContext, useState, useEffect, useRef } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import '../styles/Sidebar.css';
import brandLogo from '../assets/elon_logo.png';
import defaultPhoto from '../assets/fotoPerfil.jpg';
import { AuthContext } from '../context/AuthContext';

function Sidebar({ onLogoClick, onLinkClick }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user, logout } = useContext(AuthContext);
  const [photo, setPhoto] = useState(defaultPhoto);
  const [menuOpen, setMenuOpen] = useState(false);
  const sidebarRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (user && user.id_usuario) {
      const fetchPhoto = async () => {
        try {
          const response = await fetch(`${process.env.REACT_APP_API_URL}/api/usuarios/${user.id_usuario}/foto`);
          
          if (!response.ok) {
            setPhoto(defaultPhoto);
            return;
          }
          
          const photoBlob = await response.blob();
          if (photoBlob.size === 0) {
            setPhoto(defaultPhoto);
            return;
          }

          const contentType = response.headers.get('Content-Type');
          if (!contentType || !contentType.includes('image')) {
            setPhoto(defaultPhoto);
            return;
          }

          const photoURL = URL.createObjectURL(photoBlob);
          setPhoto(photoURL);

        } catch (error) {
          setPhoto(defaultPhoto);
        }
      };
      fetchPhoto();
    } else {
      setPhoto(defaultPhoto);
    }
  }, [user]);

  // Cerrar menú si se hace clic fuera del sidebar
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuOpen && sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuOpen]);

  // Manejar navegación por teclado
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!menuOpen) return;

      switch (event.key) {
        case 'Escape':
          setMenuOpen(false);
          break;
        case 'ArrowDown':
          event.preventDefault();
          const menuItems = menuRef.current?.querySelectorAll('.menu-item');
          const currentIndex = Array.from(menuItems).indexOf(document.activeElement);
          const nextIndex = (currentIndex + 1) % menuItems.length;
          menuItems[nextIndex]?.focus();
          break;
        case 'ArrowUp':
          event.preventDefault();
          const items = menuRef.current?.querySelectorAll('.menu-item');
          const currentIdx = Array.from(items).indexOf(document.activeElement);
          const prevIndex = (currentIdx - 1 + items.length) % items.length;
          items[prevIndex]?.focus();
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [menuOpen]);

  const handleLogoClick = () => {
    if (pathname !== '/dashboard') {
      navigate('/dashboard');
    }
  };

  const handleMenuToggle = (event) => {
    event.preventDefault();
    setMenuOpen((prev) => !prev);
  };

  const handleLogout = async () => {
    const token = localStorage.getItem('token');
    try {
      await fetch(`${process.env.REACT_APP_API_URL}/api/auth/logout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
    }
    logout();          
    setMenuOpen(false);  
    navigate('/login');  
  };

  const handleEditProfile = () => {
    navigate('/editar-perfil');   
    setMenuOpen(false);           
  };

  const isCuentasActive = () => {
    return pathname === '/cuentas' || 
           pathname === '/cuentas/agregar' || 
           pathname.startsWith('/cuentas/editar/');
  };

  const isProspectosActive = () => {
    return pathname === '/prospectos' || 
           pathname.startsWith('/prospectos/agregar') || 
           pathname.startsWith('/prospectos/editar/') || 
           pathname === '/promociones';
  };

  const isCursosActive = () => {
    return pathname === '/cursos' || 
           pathname === '/cursos/agregar' || 
           pathname.startsWith('/cursos/editar/');
  };

  const isAlumnosActive = () => {
    return pathname === '/alumnos' || 
           pathname === '/alumnos/agregar' || 
           pathname.startsWith('/alumnos/editar/');
  };

  const isHistorialActive = () => {
    return pathname === '/historial';
  };

  // const isBajasActive = () => {
  //   return pathname === '/bajas' || 
  //          pathname === '/bajas/agregar' || 
  //          pathname.startsWith('/bajas/editar/');
  // };

  // const isRedesSocialesActive = () => {
  //   return pathname === '/redes' || 
  //          pathname === '/redes/agregar' || 
  //          pathname.startsWith('/redes/editar/');
  // };

  const navigationItems = [
    { path: '/dashboard', icon: 'fas fa-home', label: 'Dashboard' },
    { path: '/prospectos', icon: 'fas fa-user-friends', label: 'Prospectos', isActive: isProspectosActive },
    { path: '/alumnos', icon: 'fas fa-users', label: 'Alumnos', isActive: isAlumnosActive },
    { path: '/cursos', icon: 'fas fa-graduation-cap', label: 'Cursos', isActive: isCursosActive },
    // { path: '/bajas', icon: 'fas fa-user-times', label: 'Bajas', isActive: isBajasActive },
    // { path: '/redes', icon: 'fas fa-share-alt', label: 'Redes Sociales', isActive: isRedesSocialesActive },
    { path: '/cuentas', icon: 'fas fa-user-cog', label: 'Cuentas', isActive: isCuentasActive, adminOnly: true },
    { path: '/historial', icon: 'fas fa-history', label: 'Historial', isActive: isHistorialActive, adminOnly: true },
  ];

  return (
    <aside className="sidebar" ref={sidebarRef}>
      <div className="sidebar-header">
        <img 
          src={brandLogo} 
          onClick={handleLogoClick} 
          alt="Brand Logo" 
          className="sidebar-logo"
        />
      </div>
      
      <nav className="sidebar-nav">
        <ul className="nav-list">
          {navigationItems.map((item) => {
            // Verificar permisos de administrador
            if (item.adminOnly && user?.rol !== 'administrador') return null;
            
            return (
              <li key={item.path} className="nav-item">
                <NavLink
                  to={item.path}
                  onClick={() => {
                    onLinkClick();
                    setMenuOpen(false);
                  }}
                  className={({ isActive }) => 
                    `nav-link ${(item.isActive ? item.isActive() : isActive) ? 'active' : ''}`
                  }
                >
                  <i className={item.icon}></i>
                  <span>{item.label}</span>
                </NavLink>
              </li>
            );
          })}
        </ul>
      </nav>

      <div className="sidebar-footer">
        <button 
          className="user-info" 
          onClick={handleMenuToggle}
          aria-expanded={menuOpen}
          aria-haspopup="true"
          aria-controls="user-menu"
        >
          <img 
            src={photo}
            alt="User" 
            className="user-avatar"
          />
          <div className="user-details">
            <span className="user-name">{user?.nombre} {user?.apellido_paterno}</span>
            <span className="user-role">{user?.rol}</span>
          </div>
          <i className={`fas fa-chevron-${menuOpen ? 'up' : 'down'} menu-arrow`}></i>
        </button>

        {menuOpen && (
          <div 
            id="user-menu"
            className="user-dropdown-menu"
            ref={menuRef}
            role="menu"
            aria-label="User menu"
          >
            <button 
              className="menu-item" 
              onClick={handleEditProfile}
              role="menuitem"
            >
              <i className="fas fa-user-edit"></i>
              Editar Perfil
            </button>
            <button 
              className="menu-item" 
              onClick={handleLogout}
              role="menuitem"
            >
              <i className="fas fa-sign-out-alt"></i>
              Cerrar Sesión
            </button>
          </div>
        )}
      </div>
    </aside>
  );
}

export default Sidebar;
